import { getWindow } from '@acorns/web-utils'

import createEnvironment from 'config/env'
import {
  suitabilityQuestionsInvestRevamp,
  suitabilityQuestionsLaterRevamp,
} from 'constants/feature-keys'
import {
  OnboardingRequirement,
  OnboardingType,
  useOnboardingRequirementsQuery,
} from 'generated/types'

import { FlowProviderActionProps, FlowRoute } from './types'
import { getFlow } from './utils'

const INVESTOR_QUESTIONS_REQUIREMENTS = [
  OnboardingRequirement.AFFILIATED_WITH_BROKER_DEALER,
  OnboardingRequirement.SHAREHOLDER_OR_DIRECTOR,
  OnboardingRequirement.SUBJECT_TO_BACKUP_WITHHOLDING,
]

export const CONTINUE_CHECKING_ONBOARDING_PATH = 'spend/signup/occupation'
export const CONTINUE_CHECKING_ONBOARDING_PATH_MOC = 'moc/occupation'
export const CONTINUE_EARLY_ONBOARDING_PATH = 'early/onboarding/beneficiary_details'

const environment = createEnvironment()
const webAppUrl = environment.get('webAppUrl')

export interface StateInput {
  allOnboardingRequirements: OnboardingRequirement[]
  totalNumRequirements?: number
  referrer?: FlowRoute
}

export const mapRequirementToRoute = (
  { allOnboardingRequirements }: StateInput,
  props: FlowProviderActionProps,
) => {
  const flow = getFlow()
  const hasKycReqs = allOnboardingRequirements?.some((req) => {
    return (
      req === OnboardingRequirement.DATE_OF_BIRTH ||
      req === OnboardingRequirement.RESIDENTIAL_ADDRESS ||
      req === OnboardingRequirement.MAILING_ADDRESS ||
      req === OnboardingRequirement.SOCIAL_SECURITY_NUMBER ||
      req === OnboardingRequirement.AFFILIATED_WITH_BROKER_DEALER ||
      req === OnboardingRequirement.SHAREHOLDER_OR_DIRECTOR ||
      req === OnboardingRequirement.SUBJECT_TO_BACKUP_WITHHOLDING
    )
  })
  const isInvestRevampEnabled = props.toggles.isFeatureEnabled(suitabilityQuestionsInvestRevamp)
  const isLaterRevampEnabled = props.toggles.isFeatureEnabled(suitabilityQuestionsLaterRevamp)

  if (
    (!isInvestRevampEnabled && hasKycReqs === false && flow === 'investOnboarding') ||
    (!isLaterRevampEnabled && hasKycReqs === false && flow === 'laterOnboarding')
  ) {
    allOnboardingRequirements.length = 0
  }

  if (allOnboardingRequirements === undefined || allOnboardingRequirements.length === 0) {
    if (flow === 'investOnboarding') {
      // When the toggle is on, there's a chance that there won't be any onboarding requirements
      // This usually means that the invest account already exists
      // In that case, it would route from invest interstitial to kids investing question
      // To prevent this, we need to guard against the invest interstitial
      if (isInvestRevampEnabled) {
        return props.location?.pathname === FlowRoute.InvestInterstitial
          ? FlowRoute.RecurringInvestment
          : FlowRoute.KidsInvesting
      }
      return FlowRoute.SuitabilityQuestionsRoot
    } else if (flow === 'checkingOnboarding') {
      getWindow().location.replace(`${webAppUrl}/${CONTINUE_CHECKING_ONBOARDING_PATH}`)
      return
    } else if (flow === 'mocOnboarding') {
      getWindow().location.replace(`${webAppUrl}/${CONTINUE_CHECKING_ONBOARDING_PATH_MOC}`)
      return
    } else if (flow === 'earlyOnboarding') {
      getWindow().location.replace(`${webAppUrl}/${CONTINUE_EARLY_ONBOARDING_PATH}`)
      return
    } else if (flow === 'laterOnboarding') {
      if (isLaterRevampEnabled) {
        return props.location?.pathname === FlowRoute.LaterInterstitial
          ? FlowRoute.RecurringInvestment
          : FlowRoute.PreviewPortfolio
      }
      return FlowRoute.LaterSuitabilityQuestions
    }

    throw new Error(`There are no onboarding requirements left to fulfill for flow ${flow}`)
  }

  if (allOnboardingRequirements.includes(OnboardingRequirement.DATE_OF_BIRTH)) {
    return FlowRoute.DateOfBirth
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.RESIDENTIAL_ADDRESS)) {
    return FlowRoute.ContactInfo
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.MAILING_ADDRESS)) {
    return FlowRoute.AddressPreview
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.SOCIAL_SECURITY_NUMBER)) {
    return FlowRoute.SSN
  } else if (
    allOnboardingRequirements.some((requirement) =>
      INVESTOR_QUESTIONS_REQUIREMENTS.includes(requirement),
    )
  ) {
    return FlowRoute.InvestorQuestions
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.EMPLOYMENT_STATUS)) {
    return FlowRoute.EmploymentStatus
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.MARITAL_STATUS)) {
    return FlowRoute.MaritalStatus
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.FILING_TYPE)) {
    return FlowRoute.TaxFilingType
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.COMBINED_ANNUAL_INCOME)) {
    return FlowRoute.CombinedAnnualIncome
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.ANNUAL_INCOME)) {
    return FlowRoute.AnnualIncome
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.SELF_EMPLOYED_INCOME)) {
    return FlowRoute.SelfEmployedIncome
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.EMPLOYEES)) {
    return FlowRoute.Employees
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.NET_WORTH)) {
    return FlowRoute.NetWorth
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.INVESTMENT_GOAL)) {
    return FlowRoute.InvestmentGoal
  } else if (allOnboardingRequirements.includes(OnboardingRequirement.FINANCIAL_EDUCATION)) {
    return FlowRoute.FinancialEducation
  }

  throw new Error(
    `Onboarding requirement is not mapped to route. Requirements left ${allOnboardingRequirements.toString()}`,
  )
}

interface UseOnboardingRequirementsResponse {
  allOnboardingRequirements: OnboardingRequirement[] | undefined
  isOnboardingRequirementsLoading: boolean
}

export const useOnboardingRequirements = (
  onboardingType: OnboardingType,
): UseOnboardingRequirementsResponse => {
  const { data, loading } = useOnboardingRequirementsQuery({
    fetchPolicy: 'network-only',
    variables: { onboardingType },
  })

  // We will not show eCBSV if it's included as requirement but SSN is not due to weirdness of eCBSV modal popping up
  // randomly without being on the SSN page
  const shouldFilterEcbsv =
    data?.onboardingRequirements.includes(OnboardingRequirement.ECBSV) &&
    !data?.onboardingRequirements.includes(OnboardingRequirement.SOCIAL_SECURITY_NUMBER)
  let allOnboardingRequirements = data?.onboardingRequirements
  if (shouldFilterEcbsv) {
    allOnboardingRequirements = allOnboardingRequirements.filter(
      (requirement) => requirement !== OnboardingRequirement.ECBSV,
    )
  }

  return {
    allOnboardingRequirements,
    isOnboardingRequirementsLoading: loading,
  }
}
